import { useCallback, useEffect, useRef, useState } from 'react'

import { getMediaUrl, ProductSliderMedia } from '../media'

type UseProductSliderVideoParams = {
	media?: ProductSliderMedia[] | null
	initialSlide?: number
	onSlideChange?: (index: number) => void
}

export const useProductSliderVideo = ({ media, initialSlide = 0, onSlideChange }: UseProductSliderVideoParams) => {
	const [activeSlide, setActiveSlideState] = useState(initialSlide)
	const [loadedVideoIndexes, setLoadedVideoIndexes] = useState<Set<number>>(() =>
		media?.[initialSlide]?.type === 'video' ? new Set([initialSlide]) : new Set()
	)
	const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({})

	useEffect(() => {
		setActiveSlideState(initialSlide)
		setLoadedVideoIndexes(media?.[initialSlide]?.type === 'video' ? new Set([initialSlide]) : new Set())
	}, [initialSlide, media])

	const setActiveSlide = useCallback(
		(index: number) => {
			onSlideChange?.(index)
			setActiveSlideState(index)

			if (media?.[index]?.type === 'video') {
				setLoadedVideoIndexes(prev => new Set(prev).add(index))
			}
		},
		[media, onSlideChange]
	)

	useEffect(() => {
		Object.entries(videoRefs.current).forEach(([index, video]) => {
			if (!video) return

			const slideIndex = Number(index)
			const shouldPlay = slideIndex === activeSlide && loadedVideoIndexes.has(slideIndex)

			if (shouldPlay) {
				video.muted = true
				void video.play().catch(() => undefined)
				return
			}

			video.pause()
			video.currentTime = 0
		})
	}, [activeSlide, loadedVideoIndexes])

	const getVideoProps = useCallback(
		(index: number, src: string, poster?: string) => ({
			ref: (node: HTMLVideoElement | null) => {
				videoRefs.current[index] = node
			},
			src: loadedVideoIndexes.has(index) ? getMediaUrl(src) : undefined,
			poster: poster ? getMediaUrl(poster) : undefined,
			autoPlay: activeSlide === index && loadedVideoIndexes.has(index),
			muted: true,
			preload: 'none' as const,
			playsInline: true
		}),
		[activeSlide, loadedVideoIndexes]
	)

	return {
		activeSlide,
		setActiveSlide,
		getVideoProps
	}
}
